import { useCallback, useEffect, useMemo, useRef, useState, type RefObject } from "react";
import { ArrowLeftIcon, Loader2Icon, Settings2Icon } from "lucide-react";
import { toast } from "sonner";
import {
  ApiError,
  handleExtensionRequest,
  isExtensionRequest,
  randomHex,
  summarizeCall,
  type ApiContext,
  type CapRow,
  type ExtensionReply,
} from "@woodpecker/protocol";
import { Button } from "../ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, PageHeader } from "../ui/card";
import { Table, TBody, TD, TH, THead, TR } from "../ui/table";
import { cn } from "../lib/cn";
import { DEFAULT_EXTENSION_SANDBOX } from "../lib/config";
import { useLang, useT } from "../i18n";
import { useStore, type Store } from "../store/store";
import { sessionOf } from "../adapters/wallet";
import { navigate } from "../components/Shell";
import { SignedOut } from "../components/common";
import { apiErrorOf, paidExtConfigWrite, paidServiceCall, paidStorePut } from "../adapters/extension-write";
import { StoreClient } from "../adapters/clients";
import { useIdentitySignIn } from "../components/WalletBlock";
import { loadInstalledOne, type InstalledView } from "../adapters/installed";
import { CapsLine, ExtensionManagePage, useCapsOf, useStoreBinding, useUninstall } from "./Extensions";

interface LogEntry {
  id: number;
  time: string;
  method: string;
  summary: string;
  ok: boolean;
}

interface Latest {
  store: Store;
  lang: string;
  nonce: string; // the nonce of this frame load (issue #21): the entry url's fragment, sent back in every request
  view: InstalledView;
  caps: CapRow[];
  refreshCaps: () => Promise<CapRow[]>;
  t: ReturnType<typeof useT>;
}

/** The Console's side of the API (D76): the actions behind the methods, read from the latest state at each call. */
function contextOf(l: Latest): ApiContext {
  const { store, view, lang, t } = l;
  const { record } = view;
  const m = view.manifest!;
  const memoryEntry = store.entry(store.settings.defaultMemory);
  const payer = store.extensionIdentityFor(record.id);
  const wallet = store.wallet;
  const need = <T,>(v: T | null | undefined, what: string): T => {
    if (v === null || v === undefined) throw new ApiError("failed", what);
    return v;
  };
  return {
    manifest: m,
    nonce: l.nonce,
    ready: async () => ({
      wallet: { address: need(wallet, t("tab.noIdentity")).address, handle: store.walletHandle },
      extension: { id: record.id, name: m.name, version: record.version, identity: record.identity },
      lang,
      theme: store.theme,
      permissions: m.permissions,
      caps: l.caps,
      store: record.store ?? null,
      services: (m.services ?? []).map((name) => {
        const e = store.entry(name);
        return { name, token: e?.token ?? "", price: e ? `${e.price.max} ${e.token}` : "" };
      }),
    }),
    caps: () => l.refreshCaps(),
    configGet: async () => (await need(store.memory, t("tab.noSession")).extConfig(record.id)).config,
    configPut: async (config) => {
      const memory = need(store.memory, t("tab.noSession"));
      const entry = need(memoryEntry, t("tab.noSession"));
      const w = need(wallet, t("tab.noIdentity"));
      const p = need(payer, t("tab.noIdentity"));
      try {
        const r = await paidExtConfigWrite({
          memory,
          entry,
          wallet: w.address,
          payer: p,
          chainId: store.config.chainId,
          contractA: store.config.contractA,
          id: record.id,
          config,
        });
        return r.receipt;
      } catch (e) {
        throw apiErrorOf(e);
      }
    },
    // the store chosen at install (D74): its session is the owner's, the put is paid by the extension's identity
    store: (() => {
      const name = record.store;
      const e = name ? store.entry(name) : undefined;
      if (!name || !e || !wallet) return null;
      const client = new StoreClient(e.origin, wallet.address, store.config.chainId, e.name);
      const session = () => {
        if (!client.session) throw new ApiError("failed", t("tab.storeNoSession", { name }));
        return client;
      };
      return {
        put: async (file) => {
          const p = need(payer, t("tab.noIdentity"));
          const bytes = file.bytes ?? new Uint8Array(0);
          try {
            const r = await paidStorePut({
              store: session(),
              entry: e,
              wallet: wallet.address,
              payer: p,
              chainId: store.config.chainId,
              contractA: store.config.contractA,
              id: record.id,
              name: file.name,
              bytes,
              type: file.type,
            });
            return r.receipt;
          } catch (e2) {
            throw apiErrorOf(e2);
          }
        },
        list: async () => {
          try {
            return (await session().list(record.id)).files.filter((f) => f.state === "confirmed");
          } catch (e2) {
            throw apiErrorOf(e2);
          }
        },
        delete: async (name) => {
          try {
            await session().delete(record.id, name);
          } catch (e2) {
            throw apiErrorOf(e2);
          }
        },
      };
    })(),
    call: async (service, route, body) => {
      const e = need(store.entry(service), `${service} is not in the directory`);
      const w = need(wallet, t("tab.noIdentity"));
      const p = need(payer, t("tab.noIdentity"));
      try {
        return await paidServiceCall({
          entry: e,
          wallet: w.address,
          payer: p,
          chainId: store.config.chainId,
          contractA: store.config.contractA,
          route,
          body,
        });
      } catch (e2) {
        throw apiErrorOf(e2);
      }
    },
    walletRequest: async () => {
      throw new ApiError("permission_denied", t("tab.devicesDeferred"));
    },
    toast: (text) => toast(`${m.name}: ${text}`),
    balances: async () => store.balances,
    authorizations: async () => [
      ...Object.entries(store.auths)
        .filter(([key]) => !key.startsWith("ext:"))
        .map(([title, rows]) => ({ title, rows })),
      { title: record.id, identity: record.identity, rows: l.caps },
    ],
  };
}

/**
 * The bridge of one tab (D68, D76): one listener for the life of the frame; a request from the frame's window that
 * carries the nonce of this load is answered through the handler with the Console's actions (one without it gets
 * `bad_nonce`: a page the extension navigated to, issue #21); the theme, the caps and the language go as events.
 */
function useExtensionBridge(
  frame: RefObject<HTMLIFrameElement | null>,
  view: InstalledView,
  nonce: string,
  caps: CapRow[],
  refreshCaps: () => Promise<CapRow[]>,
  onLog: (e: Omit<LogEntry, "id" | "time">) => void,
) {
  const store = useStore();
  const { lang } = useLang();
  const t = useT();
  const latest = useRef<Latest>({ store, lang, nonce, view, caps, refreshCaps, t });
  useEffect(() => {
    latest.current = { store, lang, nonce, view, caps, refreshCaps, t };
  });
  const post = useCallback((msg: unknown) => frame.current?.contentWindow?.postMessage(msg, "*"), [frame]);
  const capsKey = JSON.stringify(caps);
  useEffect(() => {
    post({ wp: 1, event: "caps", data: { caps: JSON.parse(capsKey) as CapRow[] } });
  }, [capsKey, post]);
  useEffect(() => {
    post({ wp: 1, event: "theme", data: { theme: store.theme } });
  }, [store.theme, post]);
  useEffect(() => {
    post({ wp: 1, event: "lang", data: { lang } });
  }, [lang, post]);
  useEffect(() => {
    const onMessage = (e: MessageEvent) => {
      const w = frame.current?.contentWindow;
      if (!w || e.source !== w || !isExtensionRequest(e.data)) return;
      const req = e.data;
      void handleExtensionRequest(req, contextOf(latest.current)).then((reply: ExtensionReply) => {
        w.postMessage(reply, "*");
        if (req.method !== "ready")
          onLog({ method: req.method, summary: summarizeCall(req.method, req.params ?? {}, reply), ok: reply.ok });
      });
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [frame, onLog]);
}

function Back() {
  const t = useT();
  return (
    <a
      href="#/extensions"
      className="inline-flex w-fit items-center gap-1 text-sm text-muted-foreground hover:underline"
    >
      <ArrowLeftIcon className="size-4" />
      {t("tab.back")}
    </a>
  );
}

function Frame({ view, onChanged }: { view: InstalledView; onChanged: () => void }) {
  const { config, wallet, setExtensionEnabled, entry } = useStore();
  const t = useT();
  const identitySignIn = useIdentitySignIn();
  const storeBinding = useStoreBinding();
  const storeEntry = view.record.store ? entry(view.record.store) : undefined;
  const [storeSession, setStoreSession] = useState(
    !!storeEntry && !!wallet && !!sessionOf(storeEntry.origin, wallet.address),
  );
  // a store needs a session before the extension's first put: the identity's, silently, or the wallet's in the panel;
  // over it the namespace is bound to the extension's identity when the install left it unbound (issue #21)
  useEffect(() => {
    if (!view.record.store) return;
    void (storeSession ? Promise.resolve(true) : identitySignIn.ensure(view.record.store)).then((ok) => {
      setStoreSession(ok);
      if (ok) void storeBinding.bind(view.record, { ifUnbound: true });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view.record.store, storeSession]);
  const capsOf = useCapsOf();
  const uninstall = useUninstall();
  const { record } = view;
  const frame = useRef<HTMLIFrameElement>(null);
  const [caps, setCaps] = useState<CapRow[]>([]);
  const [log, setLog] = useState<LogEntry[]>([]);
  const [busy, setBusy] = useState(false);
  const seq = useRef(0);
  const refreshCaps = useCallback(async () => {
    const rows = await capsOf(record.id, record.identity);
    setCaps(rows);
    return rows;
  }, [capsOf, record.id, record.identity]);
  useEffect(() => {
    void refreshCaps();
  }, [refreshCaps]);
  const onLog = useCallback((e: Omit<LogEntry, "id" | "time">) => {
    setLog((list) => [{ ...e, id: ++seq.current, time: new Date().toISOString().slice(11, 19) }, ...list].slice(0, 30));
  }, []);
  // the nonce of this load of the tab goes in the entry url's fragment and comes back in every request (issue #21)
  const nonce = useMemo(() => randomHex(16), []);
  useExtensionBridge(frame, view, nonce, caps, refreshCaps, onLog);
  const mine = !!wallet && record.owner.toLowerCase() === wallet.address.toLowerCase();
  const toggle = async () => {
    setBusy(true);
    try {
      await setExtensionEnabled(record.id, !record.enabled);
      onChanged();
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <div className="mb-4 flex flex-col gap-2">
        <Back />
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex flex-col gap-1">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <span className="text-lg font-semibold">{record.name}</span>
              <span className="font-mono text-xs text-muted-foreground">{record.id}</span>
              <span className="rounded border px-1.5 font-mono text-[10px]">v{record.version}</span>
              {!record.enabled && <span className="rounded border px-1.5 text-[10px]">{t("ext.disabledBadge")}</span>}
            </div>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
              <span>
                {t("tab.identity")} <span className="font-mono text-foreground">{record.identity}</span>
              </span>
              {record.store && (
                <span>
                  {t("tab.store")} <span className="font-mono text-foreground">{record.store}</span>
                  {!storeSession && (
                    <button
                      type="button"
                      className="ml-2 underline"
                      onClick={() => void identitySignIn.ensure(record.store!).then(setStoreSession)}
                    >
                      {t("tab.storeSignIn")}
                    </button>
                  )}
                </span>
              )}
            </div>
            <CapsLine rows={caps} />
          </div>
          <div className="flex flex-wrap gap-1.5">
            {mine && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => navigate(`/extensions/${encodeURIComponent(record.id)}/manage`)}
              >
                <Settings2Icon />
                {t("ext.manage")}
              </Button>
            )}
            <Button size="sm" variant="outline" disabled={busy} onClick={() => void toggle()}>
              {busy ? <Loader2Icon className="animate-spin" /> : null}
              {record.enabled ? t("tab.disable") : t("tab.enable")}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="text-destructive"
              onClick={() => void uninstall(record, () => navigate("/extensions"))}
            >
              {t("ext.uninstall")}
            </Button>
          </div>
        </div>
      </div>
      {record.enabled ? (
        <iframe
          ref={frame}
          title={record.name}
          sandbox={config.extensionSandbox ?? DEFAULT_EXTENSION_SANDBOX}
          src={`${record.url}${view.manifest?.entry ?? "index.html"}#wp=${nonce}`}
          className={cn("h-[68svh] w-full rounded-xl border bg-background")}
        />
      ) : (
        <div className="flex h-[40svh] flex-col items-center justify-center gap-3 rounded-xl border border-dashed text-center">
          <p className="text-sm text-muted-foreground">{t("tab.disabled")}</p>
          <Button size="sm" disabled={busy} onClick={() => void toggle()}>
            {t("tab.enable")}
          </Button>
        </div>
      )}
      <p className="mt-2 text-xs text-muted-foreground">
        {t("tab.frameNote")} <span className="font-mono break-all">{record.url}</span>
      </p>
      <Card size="sm" className="mt-4">
        <CardHeader>
          <CardTitle>{t("tab.log.title")}</CardTitle>
          <CardDescription>{t("tab.log.description")}</CardDescription>
        </CardHeader>
        <CardContent>
          {log.length === 0 ? (
            <p className="text-xs text-muted-foreground">{t("tab.log.empty")}</p>
          ) : (
            <Table>
              <THead>
                <TR>
                  <TH className="w-20">{t("tab.col.time")}</TH>
                  <TH className="w-36">{t("tab.col.method")}</TH>
                  <TH>{t("tab.col.result")}</TH>
                </TR>
              </THead>
              <TBody>
                {log.slice(0, 12).map((e) => (
                  <TR key={e.id}>
                    <TD className="font-mono text-xs text-muted-foreground">{e.time}</TD>
                    <TD className="font-mono text-xs">{e.method}</TD>
                    <TD className={cn("font-mono text-xs break-all whitespace-normal", !e.ok && "text-destructive")}>
                      {e.summary}
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </>
  );
}

/**
 * An installed extension's tab (D68, D69): the Console keeps the frame around the page, checks the manifest at the
 * pinned url against the pinned hash before the page loads (D79), and answers the extension API.
 */
export function ExtensionTabPage({ id }: { id: string }) {
  const { chain, signedIn, installed } = useStore();
  const t = useT();
  const record = installed.find((x) => x.id === id);
  const [view, setView] = useState<InstalledView | null | undefined>(undefined);
  const key = record ? `${record.id}|${record.url}|${record.hash}|${record.enabled}` : "";
  const load = useCallback(() => {
    if (!record) {
      setView(null);
      return;
    }
    void loadInstalledOne(chain, record).then(setView);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chain, key]);
  useEffect(() => {
    load();
  }, [load]);
  if (!signedIn)
    return (
      <>
        <PageHeader title={t("ext.title")} description={t("ext.description")} />
        <SignedOut what="signedOut.extensions" />
      </>
    );
  if (!record)
    return (
      <div className="flex flex-col gap-4">
        <Back />
        <p className="py-12 text-center text-sm text-muted-foreground">{t("tab.notInstalled")}</p>
      </div>
    );
  if (view === undefined || view === null) return <Loader2Icon className="size-5 animate-spin" />;
  if (!view.manifest)
    return (
      <div className="flex flex-col gap-4">
        <Back />
        <PageHeader title={record.name} description={record.id} />
        <p className="text-sm text-amber-700 dark:text-amber-400">{t("tab.changed", { detail: view.problem ?? "" })}</p>
      </div>
    );
  return <Frame key={key} view={view} onChanged={load} />;
}

/** `#/extensions/<id>`: the tab of an installed extension, or the management page of one this wallet published. */
export function ExtensionRoute({ id }: { id: string }) {
  const { installed } = useStore();
  return installed.some((x) => x.id === id) ? <ExtensionTabPage id={id} /> : <ExtensionManagePage id={id} />;
}
